import { routeConstants } from "./constants";
import { getMeristemConfigValue } from "./meristemConfig";
import { getRouteId } from "./meristemContext";
import { getUserCurrency } from "./plans";
import { formatPrice } from "./priceUtils";
import { getURLParams } from "./urlParams";
import { isDirect, isEnLanguage, isUS } from "./userSegment";

const BLACK_FRIDAY_PROMO_CODE_3_MONTHS_OFF = "BLACKFRIDAY3";
const BLACK_FRIDAY_PROMO_CODE_6_MONTHS_OFF = "BLACKFRIDAY6";
export type BlackFridayPromo =
  | typeof BLACK_FRIDAY_PROMO_CODE_3_MONTHS_OFF
  | typeof BLACK_FRIDAY_PROMO_CODE_6_MONTHS_OFF;

function isUserSegmentEligible() {
  return isEnLanguage();
}

function isRouteEligible() {
  const routeId = getRouteId();
  return [
    routeConstants.exsf01,
    routeConstants.exsf01d,
    routeConstants.affiliateTrialNPB,
  ].includes(routeId);
}

const VALID_UTM_CONTENT = ["1863086"];
const VALID_UTM_SOURCE = [
  "360374",
  "312695",
  "59530",
  "121977",
  "332949",
  "1318738",
  "118767",
];

export function isBlackFridayPromotion() {
  const urlParams = getURLParams();
  const promo = urlParams?.promo;
  const utm_content = urlParams?.utm_content;
  const utm_source = urlParams?.utm_source;

  return (
    isUserSegmentEligible() &&
    isRouteEligible() &&
    (promo === "3MonthBFCM2023" ||
      (promo === "6MonthBFCM2023" && isUS()) ||
      VALID_UTM_CONTENT.includes(utm_content) ||
      VALID_UTM_SOURCE.includes(utm_source))
  );
}

export function getBlackFridayPromocode(): BlackFridayPromo {
  const forcedPlanDuration = getBlackFridayForcedPlanDuration();
  return forcedPlanDuration === 12
    ? BLACK_FRIDAY_PROMO_CODE_6_MONTHS_OFF
    : BLACK_FRIDAY_PROMO_CODE_3_MONTHS_OFF;
}

export function isBlackFridayPromocode(
  initialPromocode: string
): initialPromocode is BlackFridayPromo {
  return [
    BLACK_FRIDAY_PROMO_CODE_3_MONTHS_OFF,
    BLACK_FRIDAY_PROMO_CODE_6_MONTHS_OFF,
  ].includes(initialPromocode);
}

export function canApplyBlackFridayPromocode(promocode: BlackFridayPromo) {
  if (promocode === BLACK_FRIDAY_PROMO_CODE_6_MONTHS_OFF && !isUS()) {
    return false;
  }

  // Allow users to redeem the promocode on direct in case they come back later
  return isUserSegmentEligible() && (isRouteEligible() || isDirect());
}

export function getBlackFridayForcedPlanDuration(promocode?: BlackFridayPromo) {
  if (promocode) {
    return promocode === BLACK_FRIDAY_PROMO_CODE_3_MONTHS_OFF ? 6 : 12;
  }

  const urlParams = getURLParams();
  const promo = urlParams?.promo;
  if (isUS() && promo === "6MonthBFCM2023") {
    return 12;
  }

  return 6;
}

// Hardcoded post-discount prices to avoid having
// to use an api request to load the forced plan before checkout
const priceMapping = {
  USD: {
    6: 159,
    12: 179, // Only US will force a 12 month plan
  },
  CAD: {
    6: 129,
  },
  NZD: {
    6: 169,
  },
  AUD: {
    6: 149,
  },
  GBP: {
    6: 59,
  },
  EUR: {
    6: 99,
  },
};

const priceMappingDecreased = {
  USD: {
    6: 139,
    12: 159, // Only US will force a 12 month plan
  },
  CAD: {
    6: 129,
  },
  NZD: {
    6: 169,
  },
  AUD: {
    6: 149,
  },
  GBP: {
    6: 59,
  },
  EUR: {
    6: 99,
  },
};
export function getBlackFridayForcedPlanPrice() {
  const currency = getUserCurrency();
  const forcedPlanDuration = getBlackFridayForcedPlanDuration();

  const mapping = getMeristemConfigValue("isPlanPriceDecreaseActive")
    ? priceMappingDecreased
    : priceMapping;

  const rawPrice = mapping[currency]?.[forcedPlanDuration];
  return formatPrice(rawPrice, currency);
}

const promocodeMapping = {
  USD: {
    6: "BFCMUSD3",
    12: "BFCMUSD6", // Only US will force a 12 month plan
  },
  CAD: {
    6: "BFCMCAD3",
  },
  NZD: {
    6: "BFCMNZD3",
  },
  AUD: {
    6: "BFCMAUD3",
  },
  GBP: {
    6: "BFCMGBP3",
  },
  EUR: {
    6: "BFCMEUR3",
  },
};

export function getBlackFridayReplacementPromocode(
  promocode: BlackFridayPromo,
  currency: string
) {
  const planDuration = getBlackFridayForcedPlanDuration(promocode);
  return promocodeMapping[currency]?.[planDuration];
}
